import React, { useRef, useCallback } from 'react';
import { X, Download, Copy, Printer } from 'lucide-react';
import QRCode from 'react-qr-code';
import toast from 'react-hot-toast';

export default function ViewQRDialog({ open, onClose, qrCode }) {
  const qrRef = useRef(null);

  const handleCopyUrl = useCallback(() => {
    if (qrCode?.qrUrl) {
      navigator.clipboard.writeText(qrCode.qrUrl).then(() => {
        toast.success('URL copied to clipboard');
      });
    }
  }, [qrCode]);

  const handleDownload = useCallback(() => {
    if (!qrRef.current) return;
    const svg = qrRef.current.querySelector('svg');
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      canvas.width = 512;
      canvas.height = 512;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, 512, 512);
      ctx.drawImage(img, 0, 0, 512, 512);

      const link = document.createElement('a');
      link.download = `qr-${qrCode.tableName.replace(/\s+/g, '-').toLowerCase()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success('QR code downloaded');
    };

    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  }, [qrCode]);

  const handlePrint = useCallback(() => {
    if (!qrRef.current) return;
    const svg = qrRef.current.querySelector('svg');
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head><title>QR Code - ${qrCode.tableName}</title></head>
        <body style="display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:100vh;margin:0;font-family:sans-serif;">
          <h2 style="margin-bottom:8px">${qrCode.tableName}</h2>
          <p style="margin-bottom:24px;color:#666">${qrCode.floorName}</p>
          ${svgData}
          <p style="margin-top:24px;font-size:12px;color:#999;word-break:break-all;max-width:300px;text-align:center">${qrCode.qrUrl}</p>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  }, [qrCode]);

  if (!open || !qrCode) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/40" onClick={onClose} />

      {/* Dialog */}
      <div className="relative bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-sm mx-4 p-5">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-1 rounded hover:bg-slate-100 transition-colors"
        >
          <X size={16} className="text-slate-400" />
        </button>

        {/* Table Info */}
        <div className="text-center mb-4">
          <h3 className="text-sm font-semibold text-slate-900">{qrCode.tableName}</h3>
          <p className="text-xs text-slate-500">{qrCode.floorName}</p>
        </div>

        {/* QR Code */}
        <div ref={qrRef} className="flex justify-center mb-4 p-4 bg-white rounded-lg border border-slate-100">
          <QRCode value={qrCode.qrUrl} size={200} level="H" />
        </div>

        {/* QR URL */}
        <div className="mb-4 p-3 bg-slate-50 border border-slate-200 rounded-lg">
          <label className="block text-[10px] font-medium text-slate-400 uppercase tracking-wide mb-1">QR URL</label>
          <p className="text-xs text-slate-600 font-mono break-all">{qrCode.qrUrl}</p>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          <button
            onClick={handleDownload}
            className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg bg-purple-600 text-white hover:bg-purple-700 transition-colors"
          >
            <Download size={14} />
            Download
          </button>
          <button
            onClick={handleCopyUrl}
            className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 transition-colors"
          >
            <Copy size={14} />
            Copy URL
          </button>
          <button
            onClick={handlePrint}
            className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 transition-colors"
          >
            <Printer size={14} />
            Print
          </button>
        </div>
      </div>
    </div>
  );
}
